/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { css } from "styled-components";

/*
 * The Tech9IQ colour tokens (the Ops Console's assets/tech9-ui.css, the same file Customer-Login
 * and Billing carry), light on :root and dark on :root[data-theme="dark"]. Every colour in SwiftIQ
 * goes through these: ThemePalette (Theme.ts) is a map of var(--t9-...) references, so a theme
 * switch is one attribute on <html> and no re-render.
 *
 * The light set is the Ops Console's, unchanged. The dark set follows the website's theme guide
 * (docs/THEME_GUIDE.md, assets/theme-v15.css): a near-black page, dark panes, low-contrast
 * steel-blue borders, light grey-blue text and the brand accent #46C4F2 kept.
 */
export const tokens = css`
  :root {
    color-scheme: light;
    /* Brand (Tech9IQ) */
    --t9-brand-50: #eef4f7;
    --t9-brand-100: #d6e4ee;
    --t9-brand-300: #98b7c7;
    --t9-brand-500: #5b788a;
    --t9-brand-600: #4c6575;
    --t9-brand-700: #3d525f;
    --t9-brand-800: #2f3f49;
    --t9-brand-900: #212c33;
    --t9-accent-400: #6ed2f5;
    --t9-accent-500: #46c4f2;
    --t9-accent-600: #3aafe0;

    /* Status: badge fill / border / solid / text */
    --t9-success-50: #d5edd5;
    --t9-success-200: #a9d8aa;
    --t9-success-500: #71c173;
    --t9-success-700: #3a7a3b;
    --t9-warning-50: #fef5e0;
    --t9-warning-200: #fbe2a6;
    --t9-warning-500: #f39c12;
    --t9-warning-700: #b7770d;
    --t9-danger-50: #fce8e6;
    --t9-danger-200: #f6c3bd;
    --t9-danger-500: #e74c3c;
    --t9-danger-700: #c0392b;
    --t9-info-50: #d0eef9;
    --t9-info-200: #a5dcf1;
    --t9-info-500: #46c4f2;
    --t9-info-700: #1a7aa8;

    /* Neutrals (slate) */
    --t9-slate-50: #f8fafc;
    --t9-slate-100: #f1f5f9;
    --t9-slate-200: #e2e8f0;
    --t9-slate-300: #cbd5e1;
    --t9-slate-400: #94a3b8;
    --t9-slate-500: #64748b;
    --t9-slate-600: #475569;
    --t9-slate-700: #334155;
    --t9-slate-800: #1e293b;
    --t9-slate-900: #0f172a;

    /* Roles */
    --t9-bg-page: var(--t9-slate-50);
    --t9-bg-surface: #ffffff;
    --t9-bg-raised: #ffffff;
    --t9-bg-muted: var(--t9-slate-50);
    --t9-bg-sunken: var(--t9-slate-100);
    --t9-bg-hover: var(--t9-slate-50);
    --t9-border: var(--t9-slate-200);
    --t9-border-control: var(--t9-slate-300);
    --t9-text: var(--t9-slate-900);
    --t9-text-title2: var(--t9-slate-800);
    --t9-text-body: var(--t9-slate-700);
    --t9-text-muted: var(--t9-slate-600);
    --t9-text-secondary: var(--t9-slate-500);
    --t9-text-faint: var(--t9-slate-400);
    --t9-text-ghost: var(--t9-slate-300);
    --t9-text-on-fill: #ffffff;
    --t9-primary: var(--t9-brand-600);
    --t9-primary-hover: var(--t9-brand-700);
    --t9-link: var(--t9-brand-600);
    --t9-selected-bg: var(--t9-brand-50);
    --t9-selected-text: var(--t9-brand-700);
    --t9-focus: var(--t9-accent-500);
    --t9-overlay: rgba(15, 23, 42, 0.55);
    --t9-tooltip-bg: var(--t9-slate-800);
    --t9-tooltip-text: #ffffff;
    --t9-code-bg: var(--t9-slate-900);
    --t9-code-text: var(--t9-slate-100);
    --t9-shadow-card: 0 2px 8px rgba(44, 62, 80, 0.08);
    --t9-shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --t9-shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    --t9-scrollbar: var(--t9-slate-300);

    /* Charts: brand series only (UI spec 8.1), steel blues, the accent and greys. */
    --t9-series-1: var(--t9-brand-600);
    --t9-series-2: var(--t9-accent-500);
    --t9-series-3: var(--t9-brand-900);
    --t9-series-4: var(--t9-brand-300);
    --t9-series-5: var(--t9-accent-600);
    --t9-series-6: var(--t9-slate-400);
    --t9-chart-grid: var(--t9-slate-200);
    --t9-chart-tick: var(--t9-slate-500);
  }

  :root[data-theme="dark"] {
    color-scheme: dark;
    --t9-brand-50: rgba(70, 196, 242, 0.12);
    --t9-brand-100: rgba(70, 196, 242, 0.2);
    --t9-brand-300: #5b788a;
    --t9-brand-500: #6f97ad;
    --t9-brand-600: #3f80a4;
    --t9-brand-700: #4a8bb0;
    --t9-brand-800: #9fc3d6;
    --t9-brand-900: #d3e5ee;

    --t9-success-50: rgba(113, 193, 115, 0.15);
    --t9-success-200: rgba(113, 193, 115, 0.35);
    --t9-success-700: #8fd491;
    --t9-warning-50: rgba(243, 156, 18, 0.15);
    --t9-warning-200: rgba(243, 156, 18, 0.35);
    --t9-warning-700: #f5b041;
    --t9-danger-50: rgba(231, 76, 60, 0.16);
    --t9-danger-200: rgba(231, 76, 60, 0.38);
    --t9-danger-500: #ec6f62;
    --t9-danger-700: #f08a7e;
    --t9-info-50: rgba(70, 196, 242, 0.14);
    --t9-info-200: rgba(70, 196, 242, 0.35);
    --t9-info-700: #68d2f5;

    /* The slate scale turned over, so "lightest" stays "furthest from the text". */
    --t9-slate-50: #0a0f14;
    --t9-slate-100: #141b22;
    --t9-slate-200: #1f2a33;
    --t9-slate-300: #2e3c47;
    --t9-slate-400: #6b7d8a;
    --t9-slate-500: #8798a4;
    --t9-slate-600: #b7c1c9;
    --t9-slate-700: #c9d3da;
    --t9-slate-800: #e2eaef;
    --t9-slate-900: #edf3f6;

    --t9-bg-page: #05080b;
    --t9-bg-surface: #0d1217;
    --t9-bg-raised: #141b22;
    --t9-bg-muted: #0a0f14;
    --t9-bg-sunken: #141b22;
    --t9-bg-hover: #16202a;
    --t9-border: rgba(155, 193, 211, 0.16);
    --t9-border-control: rgba(155, 193, 211, 0.3);
    --t9-text-on-fill: #ffffff;
    --t9-link: #68d2f5;
    --t9-selected-bg: rgba(70, 196, 242, 0.12);
    --t9-selected-text: #8fdcf7;
    --t9-overlay: rgba(0, 0, 0, 0.7);
    --t9-tooltip-bg: #e2eaef;
    --t9-tooltip-text: #0d1217;
    --t9-code-bg: #05080b;
    --t9-code-text: #c9d3da;
    --t9-shadow-card: 0 2px 10px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.03);
    --t9-shadow-lg: 0 18px 40px rgba(0, 0, 0, 0.55);
    --t9-shadow-xl: 0 22px 70px rgba(0, 0, 0, 0.6);
    --t9-scrollbar: #2e3c47;
  }
`;

export default tokens;
